import { ComponentProps, forwardRef, useEffect, useRef, useState } from "react";
import { Link } from "@components/shared/core/Link";
import { NavigationProps, getNavPageInfo } from "./NavigationProps";
import { ThemeContextProvider } from "@components/providers/ThemeContextProvider";
import { IconMenu, IconX } from "@hawkins/assets";
import { useAnimate } from "motion/react";
import { animationCurves } from "@utils/animationCurves";
import { animate } from "motion";
import cx from "classnames";
import { mergeRefs } from "@utils/mergeRefs";
import { usePrevious } from "@react-hookz/web";
import { useForceRerender } from "@hooks/useForceRerender";
import { useLocation } from "react-router";
import { useResizeObserver } from "@react-hookz/web";
import { ClosingShade } from "./ClosingShade";
import { NetflixAdsLogo } from "@components/shared/NetflixAdsLogo";
import { Button } from "@components/shared/core/Button";
import { useGetPathnameMinusLocale } from "@hooks/useGetPathnameMinusLocale";
import { useLenis } from "lenis/react";
import { useImagePreload } from "@hooks/useImagePreload";

export const MobileNavigation = ({ root, isOverFooter = false, ...props }: NavigationProps) => {
  const lenis = useLenis();
  const pathname = useGetPathnameMinusLocale();

  const [open, setOpen] = useState(false);

  // Managing render data
  const children = root?.itemsCollection?.items ?? [];
  const homepageLink = children[0];
  const [ctaLink] = children.slice(-1);

  // Ensure the menu closes when a link is selected
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location]);


  // Animation logic for the container background
  // ensuring the background grows and shrinks with
  // the content
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement>(null);
  const boundingBoxRef = useRef<HTMLDivElement>(null);
  const [forceCount, forceRerender] = useForceRerender();
  useResizeObserver(document.documentElement, () => forceRerender());
  useEffect(() => {
    const openButton = openButtonRef.current,
      menuContainer = menuContainerRef.current,
      boundingBox = boundingBoxRef.current;
    if (!openButton || !menuContainer || !boundingBox) return;

    animate(
      boundingBox,
      {
        opacity: 1,
        width: `${(open ? menuContainer : openButton).clientWidth}px`,
        height: `${(open ? menuContainer : openButton).clientHeight}px`,
        borderTopLeftRadius: open ? 0 : 8,
        borderTopRightRadius: open ? 0 : 8,
        marginLeft: open ? -18 : 0,
        marginRight: open ? -18 : 0,
        marginTop: open ? -18 : 0,
      },
      animationCurves.default
    );
  }, [open, forceCount]);

  return (
    <>
      <ClosingShade
        isOpen={open}
        onClose={() => setOpen(false)}
        className="z-[90]"
      />
      <ThemeContextProvider
        theme={{
          palette: "red",
          background: "neutral-light",
          foreground: "neutral-light",
        }}
        className="text-palette-current-foreground"
      >
        <div className="py-4.5 container-base flex w-full justify-between fixed top-0 z-[100] rounded-b-lg">
          {(() => {
            const homeInfo = getNavPageInfo(homepageLink);
            if (!homeInfo) return null;
            return (
            <div className="h-10 w-28 flex justify-center items-center">
              <Link
                href={`/${homeInfo.slug ?? ""}`}
                aria-label={homeInfo.navigationAccessibilityLabel ?? ""}
                onClick={() => {
                  if (pathname === "/") {
                    lenis?.scrollTo(0);
                  }
                }}
              >
                <ThemeContextProvider
                  className="flex justify-center items-center w-28 h-10"
                  theme={{
                    palette: "red",
                    foreground: isOverFooter ? "neutral-light" : "true",
                    background: "dark",
                  }}
                >
                  <NetflixAdsLogo
                    className="transition-colors w-full text-palette-current-foreground"
                    secondarySectionProps={{
                      className: cx(
                        "transition-colors",
                        open 
                          ? "text-brand-black" 
                          : isOverFooter 
                            ? "text-neutral-light/60" 
                            : "text-brand-cream"
                      ),
                    }}
                  />
                </ThemeContextProvider>
              </Link>
            </div>
            );
          })()}
          <div className="flex justify-between items-center gap-1">
            {(() => {
              const ctaInfo = getNavPageInfo(ctaLink);
              if (!ctaInfo) return null;
              return (
              <div className="z-10">
                <Button
                  variant="light-on-true"
                  size="small"
                  asChild
                  className={cx(
                    "transition-colors",
                    isOverFooter && "!bg-neutral-dark !text-neutral-light"
                  )}
                >
                  <Link href={`/${ctaInfo.slug ?? ""}`}>
                    {ctaInfo.navigationAlternateLabel || ctaInfo.title || ""}
                  </Link>
                </Button>
              </div>
              );
            })()}

            <div className="relative">
              <button
                ref={openButtonRef}
                aria-label={root?.menuAccessibilityLabel ?? ""}
                aria-hidden={open}
                tabIndex={open ? -1 : 0}
                onClick={(e) => {
                  setOpen(true);
                  // the aria-hidden above will be blocked unless button loses focus on click
                  e.currentTarget.blur();
                }}
                className="size-10 flex justify-center items-center bg-brand-cream text-neutral-black rounded-lg hover:scale-110 focus:scale-110 active:scale-90 transition-transform"
              >
                <IconMenu size="small" />
              </button>
              <MobileMenu
                ref={menuContainerRef}
                resizeContainer={forceRerender}
                root={root}
                {...props}
                open={open}
                onClose={() => setOpen(false)}
              />
              {/* This element is the background color of the menu and the part that animates its growth. */}
              <div
                className="absolute top-0 right-0 -z-10 bg-brand-cream rounded-b-lg"
                ref={boundingBoxRef}
              />
            </div>
          </div>
        </div>
      </ThemeContextProvider>
    </>
  );
};

const sizes = {
  mobilePadding: 18,
  buttonSize: 40,
};

// prettier-ignore
const mobileMenuClipPaths = {
  closed: `polygon(
    calc(100% - ${sizes.mobilePadding + sizes.buttonSize}px) 18px,
    calc(100% - ${sizes.mobilePadding}px) ${sizes.mobilePadding}px,
    calc(100% - ${sizes.mobilePadding}px) ${sizes.mobilePadding + sizes.buttonSize}px,
    calc(100% - ${sizes.mobilePadding + sizes.buttonSize}px) ${sizes.mobilePadding + sizes.buttonSize}px
  )`,
  open: "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
}

const MobileMenu = forwardRef<
  HTMLDivElement,
  NavigationProps & {
    open: boolean;
    onClose: () => void;
    resizeContainer: () => void;
  }
>(({ root, open, onClose, resizeContainer }, ref) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  // previousOpenIndex allows us to continue to render
  // old data in React, while "display: none"-ing it in the DOM
  // when out of data. This allows it to comfortably animate out.
  const previousOpenIndex = usePrevious(openIndex);
  const children = root?.itemsCollection?.items ?? [];
  const baseMenuItems = children.slice(1, -1);
  const renderedOpenIndex = openIndex != null ? openIndex : previousOpenIndex;
  const selectedChildGroup =
    typeof renderedOpenIndex === "number" &&
    baseMenuItems[renderedOpenIndex]?.__typename === "NavigationItem"
      ? baseMenuItems[renderedOpenIndex]?.itemsCollection?.items.filter(
          (item) => item && item.__typename !== "NavigationItem"
        ) ?? null
      : null;

  // Ensures that the submenu is closed with the menu itself
  useEffect(() => {
    if (!open) {
      setOpenIndex(null);
    }
  }, [open]);

  // marker for if for the currently open submenu
  // if odd items should be on the left
  // (or otherwise on the right)
  const oddLeft = (renderedOpenIndex ?? 0 % 2) === 0;

  // animation logic
  const [scope, animate] = useAnimate<HTMLDivElement>();
  const menuRootRef = useRef<HTMLDivElement>(null);
  const closeButtonContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    (async () => {
      const container = scope.current,
        closeButtonContainer = closeButtonContainerRef.current,
        menuRoot = menuRootRef.current;
      if (!container || !closeButtonContainer || !menuRoot || !menuRoot) return;

      if (open) {
        menuRoot.style.display = "";
        animate(
          menuRoot,
          { clipPath: mobileMenuClipPaths.open },
          animationCurves.default
        );
        animate(
          closeButtonContainer,
          { scale: 1, opacity: 1 },
          animationCurves.default
        );
      } else {
        await Promise.all([
          animate(
            menuRoot,
            { clipPath: mobileMenuClipPaths.closed },
            animationCurves.default
          ),
          animate(
            closeButtonContainer,
            { scale: 0, opacity: 0 },
            animationCurves.default
          ),
        ]);
        menuRoot.style.display = "none";
      }
    })();
  }, [open, animate, scope]);

  useEffect(() => {
    (async () => {
      const container = scope.current,
        menuRoot = menuRootRef.current;
      if (!container || !menuRoot || !menuRoot) return;

      menuRoot.style.maxHeight = `${menuRoot.clientHeight}px`;

      const rootMenuItems = [
        ...container.querySelectorAll("[data-root-menu-item]"),
      ] as Array<HTMLDivElement>;

      const subMenuItems = [
        ...container.querySelectorAll("[data-sub-menu-item]"),
      ] as Array<HTMLDivElement>;

      if (openIndex != null) {
        await Promise.all(
          rootMenuItems.map((item, index) => {
            if (index === openIndex) return Promise.resolve();

            const up = index < openIndex;

            return animate(
              item,
              {
                transform: `translateY(${up ? -25 : 50}px)`,
                opacity: 0,
                ...(up && { height: 0 }),
              },
              animationCurves.half
            );
          })
        );

        rootMenuItems.forEach((item, index) => {
          if (index !== openIndex) item.style.display = "none";
        });
        subMenuItems.forEach((item) => (item.style.display = ""));
        menuRoot.style.maxHeight = "";
        resizeContainer();
        await Promise.all(
          subMenuItems.map((item, index) =>
            animate(
              item,
              { transform: "translateY(0)", opacity: 1 },
              { ...animationCurves.half, delay: 0.1 * index }
            )
          )
        );
      } else {
        await Promise.all(
          subMenuItems.map((item) =>
            animate(
              item,
              { transform: "translateY(25px)", opacity: 0 },
              animationCurves.half
            )
          )
        );
        subMenuItems.forEach((item) => (item.style.display = "none"));
        rootMenuItems.forEach((item) => (item.style.display = ""));
        await Promise.all(
          rootMenuItems.map((item, index) => {
            if (index === openIndex) return Promise.resolve();

            return animate(
              item,
              { transform: "translateY(0)", opacity: 1, height: "auto" },
              animationCurves.half
            );
          })
        );
        menuRoot.style.maxHeight = "";
        resizeContainer();
      }
    })();
  }, [open, animate, scope, openIndex, resizeContainer]);

  useImagePreload(
    () =>
      baseMenuItems
        .map(
          (item) =>
            item?.__typename === "NavigationItem" &&
            (item.itemsCollection?.items ?? []).map(
              (item) => item && item.__typename !== "NavigationItem" && getNavPageInfo(item)?.navigationImage?.url
            )
        )
        .flat()
        .filter(Boolean),
    [baseMenuItems]
  );

  return (
    <ThemeContextProvider
      ref={scope}
      theme={{
        palette: "red",
        background: "neutral-light",
        foreground: "neutral-dark",
      }}
      className="text-palette-current-foreground"
    >
      <div
        ref={mergeRefs(ref, menuRootRef)}
        className="fixed top-0 right-0 w-full max-h-[100dvh] flex flex-col items-end py-4.5 container-base gap-1 overflow-hidden"
        // the clippath animation is used to properly retract visible content
        style={{ clipPath: mobileMenuClipPaths.closed, display: "none" }}
      >
        <div ref={closeButtonContainerRef} className="shrink-0">
          <button
            onClick={onClose}
            className="rounded-lg size-10 bg-palette-current-true flex justify-center items-center hover:scale-110 active:scale-90 transition-transform text-brand-cream"
          >
            <IconX size="small" />
          </button>
        </div>
        <div className="w-full flex-1 min-h-0 overflow-y-auto" data-lenis-prevent>
          <div
            className={cx(
              "flex flex-col w-full duration-half",
              openIndex == null && "py-8 delay-half"
            )}
          >
            {baseMenuItems.map((child, index) => {
              if (!child) return null;
              const isSelected = index === openIndex;
              if (child.__typename === "NavigationItem") {
                return (
                  <button
                    key={index}
                    data-root-menu-item={index}
                    className="text-left text-heading-3 inline-flex gap-2 items-center"
                    onClick={() => {
                      setOpenIndex((currentIndex) => {
                        if (currentIndex == null) {
                          return index;
                        } else if (currentIndex == index) {
                          return null;
                        } else return index;
                      });
                    }}
                  >
                    <span className="pb-2">{child.title}</span>
                    <span className="relative flex items-center">
                      <span
                        className={cx(
                          "transition-opacity",
                          isSelected && "opacity-0"
                        )}
                      >
                        <PlusIcon aria-hidden />
                      </span>
                      <span
                        className={cx(
                          "absolute top-0 left-0 size-full transition-opacity",
                          !isSelected && "opacity-100"
                        )}
                      >
                        <MinusIcon aria-hidden />
                      </span>
                    </span>
                  </button>
                );
              } else {
                const pageInfo = getNavPageInfo(child);
                if (!pageInfo) return null;
                return (
                  <Link
                    data-root-menu-item={index}
                    key={index}
                    href={`/${pageInfo.slug ?? ""}`}
                    className="text-heading-3 pb-2"
                  >
                    {pageInfo.navigationAlternateLabel || pageInfo.title}
                  </Link>
                );
              }
            })}
          </div>
          <div className="w-full">
            {selectedChildGroup?.map((child, index) => {
              const info = getNavPageInfo(child);
              if (!info) return null;
              return (
              <div
                key={index}
                data-sub-menu-item={index}
                className={cx(
                  "w-full flex",
                  index % 2 === (oddLeft ? 0 : 1)
                    ? "justify-start"
                    : "justify-end",
                )}
                style={{
                  display: "none",
                  transform: "translateY(25px)",
                  opacity: 0,
                }}
              >
                <Link
                  href={`/${info.slug ?? ""}`}
                  className="inline-flex flex-col items-center"
                >
                  {info.navigationImage?.url && (
                    <img
                      className="w-40 h-24 object-cover rounded-lg"
                      src={info.navigationImage.url}
                      alt={info.navigationImage.title ?? ""}
                    />
                  )}
                  <span className="text-button-large uppercase max-w-40 text-center">
                    {info.navigationAlternateLabel ?? info.title}
                  </span>
                </Link>
              </div>
              );
            })}
          </div>
        </div>
      </div>
    </ThemeContextProvider>
  );
});

export const PlusIcon = (props: ComponentProps<"svg">) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    {...props}
  >
    <path d="M0 8.5H16" stroke="currentColor" strokeWidth="4" />
    <path d="M8 16.5L8 0.5" stroke="currentColor" strokeWidth="4" />
  </svg>
);

export const MinusIcon = (props: ComponentProps<"svg">) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="17"
    viewBox="0 0 16 17"
    fill="none"
    {...props}
  >
    <path d="M0 8.5H16" stroke="currentColor" strokeWidth="4" />
  </svg>
);
